// @flow

import { connect } from 'react-redux';
import { math } from 'polished';
import _ from 'lodash';
import React, { Component, Children, type Node } from 'react';
import styled from 'styled-components';

import { fetchActiveApps } from 'client/actions/ifpApps';
import { fetchCart } from 'client/actions/cart';
import {
  fetchContentfulTranslations,
  getStateConfig,
} from 'client/actions/contentful';
import { fetchEnabledFeatures } from 'client/actions/optimizely';
import { fetchUser } from 'client/actions/user';
import BREAKPOINTS from 'client/constants/styles/breakpoints';
import COLORS from 'client/constants/colors';
import CONTENTFUL_ENTRY_ID from 'client/constants/contentfulEntry';
import CookieBanner from 'client/components/banner/CookieBanner';
import CORE_PRODUCT_TYPES from 'client/constants/productTypes';
import HtmlHead from 'client/components/htmlHead';
import NpnFooter from 'client/components/npnFooter';
import StrideHeaderWrapper from 'client/components/strideHeaderWrapper';
import { getPartnerDisplayName } from 'routes/health/utils';
import { getAppByType } from 'routes/cart/util';
import { STRIDE } from 'client/constants/globals';

import 'client/styles/common.scss';
import 'client/styles/solvvy.scss';

import type { MediaType } from 'types/contentful.flow';

const BackgroundContainer = styled.div`
  min-height: 100vh;
`;

const CartPageWrapper = styled.div`
  display: flex;
  flex-flow: column;
  min-height: 100vh;

  &&& {
    .flex-grow {
      flex-grow: 1;
    }

    .footer {
      background-color: ${COLORS.GREY_4};
      color: ${COLORS.WHITE}
      font-weight: bold;
      position: inherit;
      transform: unset;

      .links a {
        color: ${COLORS.WHITE};
      }

      &:before {
        box-shadow: none;
      }
    }

    @media screen and (max-width: ${math(`${BREAKPOINTS.LARGE} - 1px`)}) {
      padding-bottom: 10rem;

      .cta-button {
        width: 100%;
      }

      .footer {
        display: none;
      }
    }

    @media screen and (max-width: ${BREAKPOINTS.MEDIUM}) {
      .pad-y-3 {
        padding: 0;
      }
    }
  }
`;

const mapStateToProps: Object = (state) => ({
  partnerDisplayName: getPartnerDisplayName(state),
});

type Props = {
  children: Node,
  partnerDisplayName: string,
};

class App extends Component<Props> {
  static fetchData({ dispatch }: { dispatch: Function }) {
    return Promise.all([
      dispatch(fetchUser()),
      dispatch(fetchCart()),
      dispatch(fetchActiveApps()),
    ])
      .then(([user, ifpApps]) => {
        const locale = _.get(user, 'locale', 'en-US');
        /*
         * Will use primaryMemberId to be consistent with feature enablement
         * for logged in and non-logged in users in the health funnel
         */
        const userId = _.get(user, 'primaryMemberId');
        const healthApp = getAppByType(ifpApps, CORE_PRODUCT_TYPES.HEALTH);
        return Promise.all([
          dispatch(
            fetchContentfulTranslations(
              CONTENTFUL_ENTRY_ID.CART_PAGE_TRANSLATIONS,
              locale
            )
          ),
          dispatch(fetchEnabledFeatures({ userId })),
        ]).then(() => {
          if (healthApp) {
            const stateCode = _.get(healthApp, 'address.state', 'DEFAULT');
            dispatch(getStateConfig(stateCode));
          }
        });
      })
      .catch((err) => console.log(err)); // eslint-disable-line no-console
  }

  render() {
    const { partnerDisplayName } = this.props;

    return (
      <BackgroundContainer>
        <CartPageWrapper>
          <StrideHeaderWrapper className="flex-grow" showPhone={false}>
            <HtmlHead title="Stride Health" description="Stride Health" />
            {Children.only(this.props.children)}
            <CookieBanner />
          </StrideHeaderWrapper>
          <NpnFooter showPhone={false} show={partnerDisplayName === STRIDE} />
        </CartPageWrapper>
      </BackgroundContainer>
    );
  }
}

export default connect(mapStateToProps, {})(App);
